import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase, storageRef } from '../index'
import { Collection } from 'constants/index'
import { signOut } from 'store/auth'
import { toast } from 'react-toastify'
import { first } from 'lodash'

export const initialState = {
  error: null,
  inProgress: false,
  vendors: [],
  vendor: null,
  currentPage: 0,
  pageSize: 10
}

const vendorSlice = createSlice({
  name: 'vendor',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setVendors: (state, action) => {
      state.vendors = action.payload
    },
    setVendor: (state, action) => {
      state.vendor = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.vendors = []
      state.vendor = null
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const getExistingExhibitorWithEmail = async (email, conferenceId) => {
  const snapshot = await FirestoreDatabase.collection(Collection.VENDORS)
    .where('email', '==', email)
    .where('conferenceId', '==', conferenceId)
    .get()

  const exhibitors = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
  return exhibitors?.length ? first(exhibitors) : null
}

export const linkSubjectToExhibitor = async (vendorId, subjectId) => {
  await FirestoreDatabase.collection(Collection.VENDORS).doc(vendorId).update({
    subjectId: subjectId
  })
}

export const addVendor = (payload, conferenceId) => async (dispatch) => {
  try {
    dispatch(vendorSlice.actions.setProgress(true))
    const { email } = payload
    const snapshot = await FirestoreDatabase.collection(Collection.VENDORS)
      .where('email', '==', email)
      .where('conferenceId', '==', conferenceId)
      .get()

    if (snapshot.empty) {
      const doc = await FirestoreDatabase.collection(Collection.VENDORS).add({
        ...payload,
        conferenceId: conferenceId
      })
      return doc.id
    } else {
      toast.error('Exhibitor with this email already exists!')
    }
  } catch (err) {
    dispatch(vendorSlice.actions.setProgress(false))
    throw err
  } finally {
    dispatch(vendorSlice.actions.setProgress(false))
  }
}

export const fetchVendorById = (vendorId) => async (dispatch) => {
  try {
    dispatch(vendorSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.VENDORS).doc(vendorId).get()
    const e = snapshot.data()
    dispatch(
      vendorSlice.actions.setVendor({
        id: snapshot.id,
        ...e
      })
    )
  } catch (err) {
    dispatch(vendorSlice.actions.setError(err))
  } finally {
    dispatch(vendorSlice.actions.setProgress(false))
  }
}

export const updateVendorById = (vendorId, payload, conferenceId) => async (dispatch) => {
  try {
    dispatch(vendorSlice.actions.setProgress(true))
    const { email } = payload
    console.log(payload)
    const snapshot = await FirestoreDatabase.collection(Collection.VENDORS)
      .where('email', '==', email)
      .where('conferenceId', '==', conferenceId)
      .get()

    const matchingSnapshot = snapshot.docs.filter((doc) => doc.id !== vendorId)
    if (!matchingSnapshot.length) {
      dispatch(vendorSlice.actions.setError(null))
      await FirestoreDatabase.collection(Collection.VENDORS).doc(vendorId).update(payload)
      return vendorId
    } else {
      toast.error('Exhibitor with this email already exists!')
    }
  } catch (err) {
    console.error(err)
    dispatch(vendorSlice.actions.setProgress(false))
    throw err
  } finally {
    dispatch(vendorSlice.actions.setProgress(false))
  }
}

export const updateVendorLogo = async (vendorId, imageName) => {
  await FirestoreDatabase.collection(Collection.VENDORS).doc(vendorId).update({
    logoImage: imageName
  })
}

export const deleteVendorById = (conferenceId, vendorId) => async (dispatch) => {
  try {
    dispatch(vendorSlice.actions.setProgress(true))

    const vendorDoc = await FirestoreDatabase.collection(Collection.VENDORS).doc(vendorId).get()
    if (vendorDoc.exists) {
      const data = { id: vendorDoc.id, ...vendorDoc.data() }
      if (data.logoImage) {
        await storageRef.child(`${conferenceId}/${Collection.VENDORS}/${data.logoImage}`).delete()
      }
      await FirestoreDatabase.collection(Collection.VENDORS).doc(vendorId).delete()
      if (data.subjectId) {
        await FirestoreDatabase.collection(Collection.SUBJECTS).doc(data.subjectId).update({
          vendorId: ''
        })
      }
      return true
    }
  } catch (err) {
    dispatch(vendorSlice.actions.setError(err))
  } finally {
    dispatch(vendorSlice.actions.setProgress(false))
  }
}

export const getVendors = (conferenceId, callback) => {
  return FirestoreDatabase.collection(Collection.VENDORS)
    .where('conferenceId', '==', conferenceId)
    .onSnapshot((documents) => {
      const vendors = []
      documents.forEach((doc) => {
        vendors.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(vendors)
    })
}

export const resetVendor = () => (dispatch) => {
  dispatch(vendorSlice.actions.setVendor(null))
}

export default vendorSlice
